import React, { useCallback, useEffect, useMemo, useState } from "react";
import Moment from "react-moment";
import { setCurrentPageTitle, setCurrentRoute } from "../../app/store/layoutUI";
import { useDispatch, useSelector } from "react-redux";
import { useLoadCandidateAppsQuery, useLoadAppFormLibraryQuery } from "../../app/store/candidateApiSlice";
import { getCurrentUser } from "../../app/store/auth";
import ViewFormModal from "../../app/components/ViewFormModal";
import { getComponentUserStatus } from "../../app/store/tools";
import { useUpdateComponentsUserStatusMutation } from "../../app/store/toolsApiSlice";

const FORM_LIBRARY_STYLES = {
  pageWrap: {
    padding: "8px 0 4px 0"
  },
  shell: {
    borderRadius: "0",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    backgroundColor: "#ffffff",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
    overflow: "hidden"
  },
  hero: {
    padding: "18px 22px",
    borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
    background: "#f8fafc"
  },
  heroRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap"
  },
  titleWrap: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  titleIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "0",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
    color: "#0f172a",
    fontSize: "14px",
    border: "1px solid rgba(226, 232, 240, 0.9)",
    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)",
    flexShrink: 0
  },
  titleCopy: {
    minWidth: 0
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: 1.25,
    color: "#0f172a",
    margin: 0
  },
  subtitle: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#64748b",
    margin: "2px 0 0 0"
  },
  refreshButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "14px",
    padding: "20px 22px"
  },
  statCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    backgroundColor: "#ffffff",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    borderRadius: "0",
    padding: "16px",
    boxShadow: "0 6px 18px rgba(15, 23, 42, 0.03)",
    transition: "all 0.2s ease"
  },
  statIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "0",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "14px",
    flexShrink: 0
  },
  statContent: {
    minWidth: 0
  },
  statLabel: {
    margin: "0 0 4px 0",
    color: "#64748b",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase"
  },
  statValue: {
    margin: 0,
    color: "#0f172a",
    fontSize: "24px",
    fontWeight: 600,
    lineHeight: 1
  },
  body: {
    padding: "0"
  },
  tableWrap: {
    overflowX: "auto",
    backgroundColor: "#ffffff"
  },
  table: {
    width: "100%",
    marginBottom: 0,
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: "13px",
    color: "#334155"
  },
  tableHeadCell: {
    borderTop: "1px solid rgba(226, 232, 240, 0.9)",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
    padding: "14px 16px"
  },
  cell: {
    padding: "14px 16px",
    borderBottom: "1px solid rgba(241, 245, 249, 1)",
    verticalAlign: "top",
    fontWeight: 600
  },
  dateText: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12.5px",
    fontWeight: 600,
    color: "#0f172a"
  },
  timeText: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "2px",
    fontWeight: 600
  },
  officerAvatar: {
    width: "36px",
    height: "36px",
    borderRadius: "0",
    background: "#f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#0f172a",
    fontSize: "13px",
    fontWeight: 600,
    border: "1px solid rgba(226, 232, 240, 0.9)",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    flexShrink: 0
  },
  personName: {
    margin: 0,
    color: "#0f172a",
    fontSize: "14px",
    fontWeight: 600,
    textTransform: "uppercase"
  },
  metaLine: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "#475569",
    fontSize: "12.5px",
    lineHeight: 1.45,
    fontWeight: 600
  },
  primaryViewButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    borderRadius: "0",
    border: "1px solid #cbd5e1",
    background: "#f1f5f9",
    color: "#2563eb",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap"
  },
  viewButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    borderRadius: "0",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap"
  },
  updateBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "18px",
    height: "18px",
    borderRadius: "0",
    marginLeft: "6px",
    background: "#ef4444",
    color: "#ffffff",
    fontSize: "11px",
    fontWeight: 800,
    padding: "0 6px"
  },
  remarksText: {
    whiteSpace: "pre-wrap",
    fontSize: "12.5px",
    lineHeight: 1.5,
    color: "#475569",
    fontWeight: 600
  },
  emptyState: {
    padding: "42px 24px",
    textAlign: "center",
    backgroundColor: "#ffffff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  },
  emptyIcon: {
    width: "64px",
    height: "64px",
    margin: "0 auto 14px auto",
    borderRadius: "0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "26px"
  },
  emptyTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "18px",
    fontWeight: 600,
    textAlign: "center"
  },
  emptyText: {
    margin: "10px auto 0 auto",
    color: "#64748b",
    fontSize: "14px",
    lineHeight: 1.6,
    fontWeight: 600,
    textAlign: "center"
  }
};

const ROUTE_CONFIG = [{ url: "", name: "Home" }];

const FormLibrary = () => {
  const [wfTask, setWfTask] = useState(null);
  const [viewFormShowHide, setViewFormShowHide] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);

  const componentUserStatusSelector = useSelector(getComponentUserStatus);

  const dispatch = useDispatch();
  const currentUser = getCurrentUser();
  const currentUserId = currentUser?.linkUser || currentUser?.id;
  const currentUserIdRaw = currentUser?.id;

  // RTK Query: Declarative Loading of Candidate Apps
  const { data: candidateApps = [] } = useLoadCandidateAppsQuery(currentUserId, { skip: !currentUserId });

  const workflow = useMemo(() => {
    if (candidateApps.length > 0) {
      const mainWorkflow = candidateApps.filter(x => x.workflowType === 1);
      return mainWorkflow.length > 0 ? mainWorkflow[0] : null;
    }
    return null;
  }, [candidateApps]);

  // RTK Query: Declarative Loading of Form Library
  const { data: appFormLibraryReport = [], refetch: refetchAppFormLibrary } = useLoadAppFormLibraryQuery(
    { wfId: workflow?.oldWorkflowId, userId: currentUserId },
    { skip: !workflow || !currentUserId }
  );

  const [updateComponentsUserStatus] = useUpdateComponentsUserStatusMutation();

  const appFormLibrary = useMemo(() => {
    if (!appFormLibraryReport) return [];
    return appFormLibraryReport.filter(
      (x) =>
        x.formType !== "ACH Information" &&
        x.formType !== "Insurance Information" &&
        x.sendStatus === true
    );
  }, [appFormLibraryReport]);

  const handleUpdateComponent = useCallback((compItems, ciid) => {
    const comp = componentUserStatusSelector.filter(x => x.compId === 22);
    let updCompItems = "";
    if (comp.length > 0) {
      updCompItems = comp[0].compItems;
    }
    const compObj = {
      compName: "Forms",
      performerId: currentUserIdRaw,
      compItems: compItems,
      ciid: ciid,
      wiid: 0,
      iiid: 0,
      compId: 22
    };
    if (compItems !== "" && compItems !== updCompItems) {
      updateComponentsUserStatus(compObj);
    }
  }, [componentUserStatusSelector, currentUserIdRaw, updateComponentsUserStatus]);

  useEffect(() => {
    dispatch(setCurrentPageTitle("Form"));
    dispatch(setCurrentRoute(ROUTE_CONFIG));
  }, [dispatch]);

  useEffect(() => {
    if (appFormLibrary.length > 0) {
      let frmItems = "";
      appFormLibrary.forEach((x, key) => {
        if (key > 0) frmItems += ",";
        frmItems += x.id;
      });

      handleUpdateComponent(frmItems, appFormLibrary[0].ciid);
    }
  }, [appFormLibrary, handleUpdateComponent]);

  const handleFormShowHide = useCallback((f) => {
    if (f === "save") {
      setViewFormShowHide(false);
      setSelectedForm(null);
      setWfTask(null);
      refetchAppFormLibrary();
    } else {
      setViewFormShowHide((prev) => !prev);
      if (f?.ciid > 0) {
        setSelectedForm(f);
        const task = { ciid: f.ciid, wiid: f.wiid, iiid: f.iiid };
        setWfTask(task);
      } else {
        setSelectedForm(null);
      }
    }
  }, [refetchAppFormLibrary]);

  const handleFormUpdate = useCallback(() => {
    // Left empty to match legacy logic
  }, []);

  const totalForms = appFormLibrary.length;

  const pendingUpdates = useMemo(() => {
    return appFormLibrary.reduce((count, item) => count + (item.updatedCount || 0), 0);
  }, [appFormLibrary]);

  const distinctReviewers = useMemo(() => {
    return new Set(appFormLibrary.map((item) => item.performerName).filter(Boolean)).size;
  }, [appFormLibrary]);

  const getInitials = (name) => {
    if (!name) return "??";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <React.Fragment>
      <div className="col-md-12" style={FORM_LIBRARY_STYLES.pageWrap}>
        <div style={FORM_LIBRARY_STYLES.shell}>
          <div style={FORM_LIBRARY_STYLES.hero}>
            <div style={FORM_LIBRARY_STYLES.heroRow}>
              <div style={FORM_LIBRARY_STYLES.titleWrap}>
                <div style={FORM_LIBRARY_STYLES.titleIcon}>
                  <i className="fa fa-files-o" aria-hidden="true"></i>
                </div>
                <div style={FORM_LIBRARY_STYLES.titleCopy}>
                  <h1 style={FORM_LIBRARY_STYLES.title}>Form Library</h1>
                  <p style={FORM_LIBRARY_STYLES.subtitle}>Review the submitted onboarding record and open it for viewing or updates.</p>
                </div>
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={FORM_LIBRARY_STYLES.refreshButton}
                  onClick={() => refetchAppFormLibrary()}
                >
                  <i className="fa fa-refresh" aria-hidden="true"></i>
                  Refresh
                </button>
              </div>
            </div>
          </div>
          <div style={FORM_LIBRARY_STYLES.body}>
            <div style={{ ...FORM_LIBRARY_STYLES.statsRow, padding: "18px 22px 18px 22px" }}>
              <div style={FORM_LIBRARY_STYLES.statCard}>
                <div style={FORM_LIBRARY_STYLES.statIcon}>
                  <i className="fa fa-files-o" aria-hidden="true"></i>
                </div>
                <div style={FORM_LIBRARY_STYLES.statContent}>
                  <div style={FORM_LIBRARY_STYLES.statLabel}>Available Forms</div>
                  <div style={FORM_LIBRARY_STYLES.statValue}>{totalForms}</div>
                </div>
              </div>
              <div style={FORM_LIBRARY_STYLES.statCard}>
                <div style={FORM_LIBRARY_STYLES.statIcon}>
                  <i className="fa fa-clock-o" aria-hidden="true"></i>
                </div>
                <div style={FORM_LIBRARY_STYLES.statContent}>
                  <div style={FORM_LIBRARY_STYLES.statLabel}>Pending Updates</div>
                  <div style={FORM_LIBRARY_STYLES.statValue}>{pendingUpdates}</div>
                </div>
              </div>
              <div style={FORM_LIBRARY_STYLES.statCard}>
                <div style={FORM_LIBRARY_STYLES.statIcon}>
                  <i className="fa fa-users" aria-hidden="true"></i>
                </div>
                <div style={FORM_LIBRARY_STYLES.statContent}>
                  <div style={FORM_LIBRARY_STYLES.statLabel}>Reviewers</div>
                  <div style={FORM_LIBRARY_STYLES.statValue}>{distinctReviewers}</div>
                </div>
              </div>
            </div>
            <div style={FORM_LIBRARY_STYLES.tableWrap}>
              <div className="table-responsive">
                <table className="table align-items-center table-hover mb-0" style={FORM_LIBRARY_STYLES.table}>
                  <thead>
                    <tr>
                      <th style={FORM_LIBRARY_STYLES.tableHeadCell}>ID</th>
                      <th style={FORM_LIBRARY_STYLES.tableHeadCell}>Date & Time Submitted</th>
                      <th style={FORM_LIBRARY_STYLES.tableHeadCell}>Review Officer</th>
                      <th style={FORM_LIBRARY_STYLES.tableHeadCell}>Onboarding Form</th>
                      <th style={FORM_LIBRARY_STYLES.tableHeadCell}>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appFormLibrary.length > 0 ? appFormLibrary.map((item, key) => (
                      <tr key={item.id || key}>
                        <td style={FORM_LIBRARY_STYLES.cell}>
                          <span style={{ color: "#0f172a", fontWeight: 700 }}>#</span>{" "}
                          <span style={{ fontWeight: 600, color: "#0f172a" }}>{item.formId || item.id || key + 1}</span>
                        </td>
                        <td style={FORM_LIBRARY_STYLES.cell}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <span style={{ fontSize: "13.5px", fontWeight: 600, color: "#0f172a" }}>
                              <Moment format="MMM Do, YYYY">
                                {item.createdDateTime}
                              </Moment>
                            </span>
                            <span style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                              <Moment format="h:mm a">
                                {item.createdDateTime}
                              </Moment>
                            </span>
                          </div>
                        </td>
                        <td style={FORM_LIBRARY_STYLES.cell}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                            <div style={FORM_LIBRARY_STYLES.officerAvatar}>
                              {getInitials(item.performerName)}
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "3px", minWidth: 0 }}>
                              <p style={FORM_LIBRARY_STYLES.personName}>{item.performerName}</p>
                              {item.email && (
                                <div style={FORM_LIBRARY_STYLES.metaLine}>
                                  <i className="fa fa-envelope-o" style={{ fontSize: "11px", color: "#94a3b8" }} aria-hidden="true"></i>
                                  {item.email}
                                </div>
                              )}
                              {(item.performerRole || item.department || item.partner) && (
                                <div style={{ ...FORM_LIBRARY_STYLES.metaLine, color: "#0f172a", fontWeight: 600 }}>
                                  <i className="fa fa-briefcase" style={{ fontSize: "11px" }} aria-hidden="true"></i>
                                  {[item.performerRole, item.department, item.partner].filter(Boolean).join(" | ")}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td style={FORM_LIBRARY_STYLES.cell}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                            <button
                              className="btn btn-sm"
                              style={FORM_LIBRARY_STYLES.primaryViewButton}
                              onClick={() => handleFormShowHide(item)}
                            >
                              <i className="fa fa-eye" aria-hidden="true"></i>
                              View Form
                              {item.updatedCount > 0 &&
                                <span style={FORM_LIBRARY_STYLES.updateBadge}>
                                  {item.updatedCount}
                                </span>}
                            </button>
                            <button
                              className="btn btn-sm"
                              style={FORM_LIBRARY_STYLES.viewButton}
                              onClick={() => handleFormShowHide(item)}
                            >
                              <i className="fa fa-pencil" aria-hidden="true"></i>
                              Edit Details
                            </button>
                          </div>
                        </td>
                        <td style={FORM_LIBRARY_STYLES.cell}>
                          <div style={FORM_LIBRARY_STYLES.remarksText}>{item.remarks || "No remarks added."}</div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="5" style={{ ...FORM_LIBRARY_STYLES.cell, padding: 0 }}>
                          <div style={FORM_LIBRARY_STYLES.emptyState}>
                            <div style={FORM_LIBRARY_STYLES.emptyIcon}>
                              <i className="fa fa-files-o" aria-hidden="true"></i>
                            </div>
                            <h4 style={FORM_LIBRARY_STYLES.emptyTitle}>No forms available</h4>
                            <p style={FORM_LIBRARY_STYLES.emptyText}>
                              Shared forms will appear here once a reviewer sends them to your workflow. When available, you can open and review them from this library.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ViewFormModal
        viewFormShowHide={viewFormShowHide}
        handleViewForm={handleFormShowHide}
        workflowTask={wfTask}
        selectedForm={selectedForm}
        handleFormUpdate={handleFormUpdate}
        allowEdit={true}
      ></ViewFormModal>
    </React.Fragment>
  );
}

export default FormLibrary;
